import type { ImmutableCandidateObjectV1 } from './failedCandidate.js';
import { selectReleasedRuntime, type ReleasedRuntimeCatalogV1,
  validateRuntimeExecutionSupportV1, type RuntimeExecutionSupportV1 } from './compositionRuntimeRelease.js';
import { validateAcceptedCompositionV1, type AcceptedCompositionV1 } from './spec/gameSpec.js';

const SHA256 = /^[a-f0-9]{64}$/;
const GENERATION = /^[1-9][0-9]{0,30}$/;

export const MAX_COMPOSITION_RUNTIME_DESCRIPTOR_BYTES = 2 * 1024 * 1024;

export interface CompositionActiveRuntimeV1 extends ImmutableCandidateObjectV1 {
  contentType: 'application/json';
}

/** Private immutable activation descriptor. The catalog may retain older
 * releases so already-accepted work remains executable after activation. */
export interface CompositionRuntimeDescriptorV1 {
  schemaVersion: 1;
  releaseId: string;
  authority: AcceptedCompositionV1;
  catalog: ImmutableCandidateObjectV1;
  support: RuntimeExecutionSupportV1;
  maintainedV2: CompositionMaintainedRuntimeV2 | null;
}

export interface CompositionMaintainedRuntimeV2 {
  releaseId: string;
  catalog: ImmutableCandidateObjectV1;
  publicationTarget: ImmutableCandidateObjectV1;
}

function record(value: unknown, keys: string[], label: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)
    || Object.getPrototypeOf(value) !== Object.prototype
    || Object.keys(value).sort().join(',') !== [...keys].sort().join(',')) {
    throw new Error(`Invalid ${label}`);
  }
  return value as Record<string, unknown>;
}

function immutableJsonObject(value: unknown, path: RegExp, maximumBytes: number,
  label: string): ImmutableCandidateObjectV1 {
  const raw = record(value, ['path', 'sha256', 'generation', 'bytes', 'contentType'], label);
  if (typeof raw.path !== 'string' || !path.test(raw.path)
    || typeof raw.sha256 !== 'string' || !SHA256.test(raw.sha256)
    || typeof raw.generation !== 'string' || !GENERATION.test(raw.generation)
    || !Number.isSafeInteger(raw.bytes) || (raw.bytes as number) < 1 || (raw.bytes as number) > maximumBytes
    || raw.contentType !== 'application/json') throw new Error(`Invalid ${label}`);
  return { path: raw.path, sha256: raw.sha256, generation: raw.generation,
    bytes: Number(raw.bytes), contentType: raw.contentType };
}

export function validateCompositionActiveRuntimeV1(value: unknown): CompositionActiveRuntimeV1 {
  const ref = immutableJsonObject(value,
    /^gs:\/\/[a-z0-9][a-z0-9._-]{1,221}\/composition-runtime-catalogs\/descriptors\/[a-f0-9]{64}\.json$/,
    MAX_COMPOSITION_RUNTIME_DESCRIPTOR_BYTES, 'composition active runtime reference');
  if (!ref.path.endsWith(`/composition-runtime-catalogs/descriptors/${ref.sha256}.json`)) {
    throw new Error('Invalid composition active runtime identity');
  }
  return ref as CompositionActiveRuntimeV1;
}

export function validateCompositionRuntimeCatalogReferenceV1(value: unknown): ImmutableCandidateObjectV1 {
  return immutableJsonObject(value,
    /^gs:\/\/[a-z0-9][a-z0-9._-]{1,221}\/composition-runtime-catalogs\/[a-f0-9]{64}\.json$/,
    4 * 1024 * 1024, 'composition runtime catalog reference');
}

function validateMaintainedRuntimeV2(value: unknown): CompositionMaintainedRuntimeV2 | null {
  if (value === null) return null;
  const raw = record(value, ['releaseId', 'catalog', 'publicationTarget'], 'maintained composition runtime');
  if (typeof raw.releaseId !== 'string' || !SHA256.test(raw.releaseId)) {
    throw new Error('Invalid maintained composition runtime identity');
  }
  const root = 'composition-runtime-catalogs/maintained/[a-f0-9]{64}';
  const catalog = immutableJsonObject(raw.catalog,
    new RegExp(`^gs://[a-z0-9][a-z0-9._-]{1,221}/${root}/catalog\\.json$`),
    4 * 1024 * 1024, 'maintained composition catalog reference');
  const publicationTarget = immutableJsonObject(raw.publicationTarget,
    new RegExp(`^gs://[a-z0-9][a-z0-9._-]{1,221}/${root}/publication-target\\.json$`),
    256 * 1024, 'maintained composition publication target reference');
  if (!catalog.path.endsWith(`/${catalog.sha256}/catalog.json`)
    || !publicationTarget.path.endsWith(`/${publicationTarget.sha256}/publication-target.json`)) {
    throw new Error('Invalid maintained composition object digest path');
  }
  return {
    releaseId: raw.releaseId,
    catalog: {
      path: catalog.path, sha256: catalog.sha256, generation: catalog.generation,
      bytes: catalog.bytes, contentType: catalog.contentType,
    },
    publicationTarget: {
      path: publicationTarget.path, sha256: publicationTarget.sha256,
      generation: publicationTarget.generation, bytes: publicationTarget.bytes,
      contentType: publicationTarget.contentType,
    },
  };
}

export async function validateCompositionRuntimeDescriptorV1(
  value: unknown,
  catalog: ReleasedRuntimeCatalogV1,
): Promise<CompositionRuntimeDescriptorV1> {
  const raw = record(value, ['schemaVersion', 'releaseId', 'authority', 'catalog', 'support', 'maintainedV2'],
    'composition runtime descriptor');
  if (raw.schemaVersion !== 1 || typeof raw.releaseId !== 'string' || !SHA256.test(raw.releaseId)) {
    throw new Error('Invalid composition runtime descriptor');
  }
  const authority = await validateAcceptedCompositionV1(raw.authority);
  if (authority.runtimeReleaseId !== raw.releaseId) throw new Error('Composition runtime descriptor authority mismatch');
  const catalogRef = validateCompositionRuntimeCatalogReferenceV1(raw.catalog);
  if (!catalogRef.path.endsWith(`/composition-runtime-catalogs/${catalog.catalogId}.json`)) {
    throw new Error('Composition runtime descriptor catalog mismatch');
  }
  const support = validateRuntimeExecutionSupportV1(raw.support);
  const maintainedV2 = validateMaintainedRuntimeV2(raw.maintainedV2);
  const bucket = (path: string) => /^gs:\/\/([^/]+)\//.exec(path)?.[1];
  if (bucket(catalogRef.path) !== bucket((maintainedV2?.catalog ?? catalogRef).path)
    || maintainedV2 && bucket(maintainedV2.publicationTarget.path) !== bucket(catalogRef.path)) {
    throw new Error('Composition runtime descriptor bucket mismatch');
  }
  await selectReleasedRuntime({
    catalog,
    pinned: {
      releaseId: authority.runtimeReleaseId,
      closureDigest: authority.closureDigest,
      compiler: { revision: authority.compilerRevision, digest: authority.compilerDigest },
    },
    support,
  });
  return structuredClone({
    schemaVersion: 1,
    releaseId: raw.releaseId,
    authority,
    catalog: {
      path: catalogRef.path, sha256: catalogRef.sha256, generation: catalogRef.generation,
      bytes: catalogRef.bytes, contentType: catalogRef.contentType,
    },
    support,
    maintainedV2,
  });
}
